/** A "More" menu for a header's less-used actions (design review 2026-11-03, ux #14 and systems #5).
 *
 * The filing's header carried four buttons and five info icons, which squeezed the title into a
 * column of four lines at 1024. The two actions that are rarely available (Export applies only to an
 * issued filing; Email is off until a sender is set up) go under one control. **A disabled item says
 * why in place**, beside its name, rather than behind an icon of its own: the reason is the part a
 * reader needs and a greyed-out word with nothing next to it is a dead end.
 *
 * A button (`aria-haspopup`, `aria-expanded`) that opens a menu: Escape and a press outside close it
 * and focus returns to the button; arrow keys move between the enabled items. It carries no copy of
 * its own, only what the caller gives each item.
 */

import { CaretDown } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import type { KeyboardEvent } from "react";
import { useId, useRef } from "react";

import { usePopup } from "../../components/ui/usePopup";
import styles from "./FilingParts.module.css";

export interface MoreItem {
  key: string;
  label: string;
  icon?: Icon;
  /** Absent for an item that cannot be taken: it is drawn disabled with `reason` beside it. */
  onSelect?: () => void;
  /** Why the item is off, in a few words. */
  reason?: string;
  testId?: string;
}

export function MoreMenu({ label = "More", items, testId = "filing-more" }: { label?: string; items: readonly MoreItem[]; testId?: string }) {
  const { open, setOpen, close, triggerRef, popupRef } = usePopup<HTMLButtonElement>();
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      const enabled = [...(listRef.current?.querySelectorAll<HTMLElement>("[role='menuitem']:not([aria-disabled='true'])") ?? [])];
      if (enabled.length === 0) return;
      event.preventDefault();
      const at = enabled.indexOf(document.activeElement as HTMLElement);
      const next = event.key === "ArrowDown" ? (at + 1) % enabled.length : (at - 1 + enabled.length) % enabled.length;
      enabled[next]?.focus();
    }
  };

  return (
    <div className={styles.more} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.moreButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
        data-testid={testId}
      >
        {label}
        <CaretDown size={12} weight="bold" aria-hidden="true" />
      </button>
      {open ? (
        <div ref={popupRef} className={styles.moreList} id={id} role="menu" aria-label={label}>
          <div ref={listRef}>
            {items.map((item) => {
              const Glyph = item.icon;
              const off = !item.onSelect;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={styles.moreItem}
                  aria-disabled={off || undefined}
                  disabled={off}
                  onClick={() => {
                    close(false);
                    item.onSelect?.();
                  }}
                  data-testid={item.testId}
                >
                  {Glyph ? <Glyph size={16} aria-hidden="true" /> : null}
                  <span>{item.label}</span>
                  {item.reason ? <span className={styles.moreReason}>{item.reason}</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
